// frontend/src/features/ups/UpsDriverStats.js
// This React component renders UPS driver stats & controls—agency selector, search box, a toggle for global search, and counters that reflect the current filters (agency + query).
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { selectAllAgencies, fetchAgencies } from '../agencies/agenciesSlice';
import { selectCountsForFilters } from '../drivers/driverSlice'; // ⬅️ new selector
import { selectFilters, setAgencyId, setQuery, setScope } from '../ui/filtersSlice';

import StatusCounters from '../../components/StatusCounters';

export default function UpsDriverStats() {
  const dispatch = useDispatch();
  const agencies = useSelector(selectAllAgencies);
  const { agencyId, q, scope } = useSelector(selectFilters);

  // Ensure agencies are loaded for the dropdown
  useEffect(() => {
    if (!agencies?.length) dispatch(fetchAgencies());
  }, [dispatch, agencies?.length]);

  // 👇 counts are NOW scoped by current filters (agency + query)
  const counts = useSelector(selectCountsForFilters);

  const onAgencyChange = (e) => dispatch(setAgencyId(e.target.value)); // '' or '1'
  const onSearchChange = (e) => dispatch(setQuery(e.target.value));    // (optionally debounce)
  const onScopeToggle  = (e) => dispatch(setScope(e.target.checked ? 'global' : 'tab'));

  const agenciesSorted = useMemo(() => {
    const list = Array.isArray(agencies) ? agencies : [];
    return [...list].sort((a, b) =>
      (a?.name ?? a?.agency_name ?? '').localeCompare(b?.name ?? b?.agency_name ?? '')
    );
  }, [agencies]);

  const getAgencyIdStr = (a) => String(a?.id ?? a?.agency_id ?? a?.agencyId ?? '');

  return (
    <div className="mb-4 space-y-3">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={agencyId === '' ? '' : String(agencyId)}
          onChange={onAgencyChange}
          className="border rounded px-2 py-1"
        >
          <option value="">All Agencies</option>
          {agenciesSorted.map((a) => {
            const idStr = getAgencyIdStr(a);
            return (
              <option key={idStr || (a?.name ?? a?.agency_name) || 'unknown'} value={idStr}>
                {a?.name ?? a?.agency_name ?? '(Unnamed)'}
              </option>
            );
          })}
        </select>

        <input
          value={q}
          onChange={onSearchChange}
          placeholder="Search drivers…"
          className="border rounded px-3 py-1 min-w-[240px] flex-1"
        />

        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={scope === 'global'}
            onChange={onScopeToggle}
          />
          <span>Search all statuses</span>
        </label>
      </div>

      {/* Figures (now reflect agency + search) */}
      <StatusCounters title="Drivers" counts={counts} />
    </div>
  );
}
